import { Injectable } from '@angular/core';
import { BazisAuthService } from '@bazis/shared/services/auth.service';
import { combineLatest, merge, of, pairwise, shareReplay, Subject, withLatestFrom } from 'rxjs';
import { AUTH_PAGE_URL, ROLE, SHARE_REPLAY_SETTINGS } from '@app/configuration.service';
import { debounceTime, filter, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { buildFilterStr } from '@bazis/utils';
import { EntData, EntList } from '@bazis/shared/models/srv.types';

@Injectable({
    providedIn: 'root',
})
export class SwAuthService extends BazisAuthService {
    private _isFirstRedirect = true;

    userEntityType = 'users.user';

    useOrganizationInfo = false;

    updateAgencyContract$ = new Subject();

    agencyContract$ = merge(this.updateAgencyContract$, this.organizationId$, this.role$).pipe(
        debounceTime(0),
        withLatestFrom(this.organizationId$, this.role$),
        switchMap(([original, organizationId, role]) =>
            organizationId &&
            (role === ROLE.landfill ||
                role === ROLE.landfillDirector ||
                role === ROLE.landfillLawyer ||
                role === ROLE.transporter)
                ? this.getAgencyContract$(organizationId)
                : of(null),
        ),
        startWith(undefined),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    defaultPageRedirect$ = this.role$.pipe(
        debounceTime(0),
        withLatestFrom(this.user$),
        startWith([null, null]),
        pairwise(),
        filter(([prevData, currentData]) => {
            const [prevRole, prevUser] = [...prevData];
            const [currentRole, currentUser] = [...currentData];
            return prevUser?.id !== currentUser?.id;
        }),
        tap(([prevData, currentData]) => {
            const [prevRole, prevUser] = [...prevData];
            const [currentRole, currentUser] = [...currentData];
            const regexp = /^\/(faq|about|legal\-documents){1}(\/.*)*$/;
            if (
                currentUser &&
                !currentUser.$snapshot.raw_password &&
                !regexp.test(location.pathname) &&
                currentUser.$snapshot.created_type_auth !== 'cert' &&
                currentUser.id !== prevUser?.id
            ) {
                this.router.navigate(['/login/init-password']);
                this._isFirstRedirect = false;
                return;
            }
            if (location.pathname.indexOf(AUTH_PAGE_URL) > -1 && !currentUser) return;
            this.redirectToDefaultPage(currentRole, currentUser);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    isIndividual$ = this.organizationInfo$.pipe(
        filter(Boolean),
        map((organizationInfo) => !!organizationInfo?.$snapshot?.is_individual),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    requestRolesForCompanyUser$ = combineLatest([this.roles$, this.role$]).pipe(
        map(([roles, role]) => {
            roles = roles.filter((v) => !v.$snapshot.is_system);
            if (role === ROLE.systemOperator) return roles;
            return roles;
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    // redirect to default page
    redirectToDefaultPage(role = null, user) {
        switch (role) {
            case ROLE.anonymous:
            case ROLE.driver:
            case ROLE.wasteAcceptor:
            case ROLE.foreman:
            case ROLE.foremanBuilder:
                this.router.navigate(['/mobile-role']);
                break;
            default:
                if (location.pathname.indexOf('access-denied') > -1 && !this._isFirstRedirect) {
                    this.router.navigate(['/']);
                } else if (
                    location.pathname.indexOf(AUTH_PAGE_URL) > -1
                    // to prevent redirect on home page when first entered page was access - denied
                ) {
                    if (user.$snapshot.roles.length === 1) this.router.navigate(['/']);
                    else this.router.navigate(['/login/select-role']);
                }
        }
        this._isFirstRedirect = false;
    }

    authRequest$() {
        return this.srv.commonGetRequest$('authing/auth');
    }

    getAgencyContract$(organizationId) {
        return this.entityService
            .getEntityList$('document.document_agency_contract', {
                params: {
                    filter: buildFilterStr({ org_owner: organizationId }),
                    sort: '-dt_created',
                },
            })
            .pipe(
                map((list: EntList) =>
                    list?.list?.length > 0
                        ? list.list.find((v) => v.$snapshot.status !== 'draft') || list.list[0]
                        : null,
                ),
            );
    }

    getAgreement$(organizationId) {
        return this.entityService
            .getEntityList$('document.document_agreement', {
                params: {
                    filter: buildFilterStr({ org_owner: organizationId }),
                    sort: '-dt_created',
                },
            })
            .pipe(
                map((list: EntList) =>
                    list?.list?.length > 0
                        ? list.list.find((v) => v.$snapshot.status !== 'draft') || list.list[0]
                        : null,
                ),
            );
    }
}
